import { createSettingMetadataFromGroupDefaults } from "../../settings-catalog/metadata";
import type {
  SettingDefinition,
  SettingMetadata,
  SettingRegistryMetadataInput,
  TeamSettingsSettingValueSource,
} from "../../settings-catalog/types";
import {
  CustomerPortalBundlesFlatTeamSettingGroupById,
  type CustomerPortalBundlesFlatTeamSettingGroupId,
} from "./groups";

type CustomerPortalBundlesSettingInput = SettingRegistryMetadataInput;

function setting(
  metadata: CustomerPortalBundlesSettingInput,
): CustomerPortalBundlesSettingInput {
  return metadata;
}

function customerPortalBundlesGroup<
  const Group extends CustomerPortalBundlesFlatTeamSettingGroupId,
  const T extends Record<string, CustomerPortalBundlesSettingInput>,
>(group: Group, settings: T): { [K in keyof T]: T[K] & { group: Group } } {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  return Object.fromEntries(
    Object.entries(settings).map(([key, value]) => [key, { ...value, group }]),
  ) as { [K in keyof T]: T[K] & { group: Group } };
}

// Keys must be real flat schema keys from ./settings.ts (wired into
// FlatTeamSettingsSchema). Each equals the team.settings.* path, dots→slashes.
const CustomerPortalBundlesRegistry = {
  ...customerPortalBundlesGroup("customer_portal_bundles.overview", {
    bundles: setting({
      label: "Bundles",
      description:
        "Product bundles that pair a parent product with its child items and per-item returnability for the customer portal.",
      synonyms: ["product bundles", "portal bundles", "bundle configuration"],
    }),
  }),
};

type CustomerPortalBundlesFlatTeamSettingKey =
  keyof typeof CustomerPortalBundlesRegistry;
// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
const CustomerPortalBundlesFlatTeamSettingKeys = Object.keys(
  CustomerPortalBundlesRegistry,
) as CustomerPortalBundlesFlatTeamSettingKey[];

export const CustomerPortalBundlesFlatTeamSettingCatalogDefinitions =
  CustomerPortalBundlesFlatTeamSettingKeys.map((key) => {
    const definition = CustomerPortalBundlesRegistry[key];
    return {
      id: key,
      groupId: definition.group,
      metadata: createSettingMetadataFromGroupDefaults({
        setting: definition,
        group: CustomerPortalBundlesFlatTeamSettingGroupById[definition.group],
      }),
      valueSource: { adapter: "teamSettings", key },
    };
  }) satisfies readonly SettingDefinition<
    CustomerPortalBundlesFlatTeamSettingKey,
    CustomerPortalBundlesFlatTeamSettingGroupId,
    TeamSettingsSettingValueSource<CustomerPortalBundlesFlatTeamSettingKey>
  >[];

export const CustomerPortalBundlesFlatTeamSettingMetadataByKey =
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  Object.fromEntries(
    CustomerPortalBundlesFlatTeamSettingCatalogDefinitions.map((definition) => [
      definition.id,
      definition.metadata,
    ]),
  ) as Record<CustomerPortalBundlesFlatTeamSettingKey, SettingMetadata>;
